import { createContext, useContext, useMemo, type ReactNode } from "react";
import type { ApiClient } from "@keydials/api-client";
import type { TabStripDisplay } from "./components/TabStrip";

/** Live-reload handlers for a segment's realtime subscription. */
export interface SegmentRealtimeHandlers {
  onEvent: () => void;
  onReconnect: () => void | Promise<void>;
}

/**
 * Live-reload handlers for a whole instance's realtime subscription. `event` is what the hub pushed — its
 * kind and coordinates, never a message body — so a subscriber can ignore traffic it doesn't render.
 */
export interface InstanceRealtimeHandlers {
  onEvent: (event?: { type?: string }) => void;
  onReconnect: () => void | Promise<void>;
}

/**
 * Host-provided services the workspace surface needs for its side-effecting features (documents,
 * discussion, subscriptions). A live host injects real, network-backed implementations; a preview
 * host injects stubs and turns the matching capabilities off so the stubs are never invoked.
 */
export interface WorkspaceServices {
  api: ApiClient;
  getAccessToken: () => string | null;
  /**
   * Subscribe a segment's discussion thread to live updates. Implemented as a React hook by the host
   * (it may call useEffect etc.), so it must be a stable function; pass a no-op hook when realtime is
   * unavailable (e.g. a draft preview).
   */
  useSegmentRealtime: (
    instanceId: string,
    sectionKey: string,
    segmentKey: string,
    handlers: SegmentRealtimeHandlers,
  ) => void;
  /**
   * Subscribe to everything pushed on one instance — the section and whole-dial Discussion transcripts
   * refresh from it (KEY-559). Same rules as {@link useSegmentRealtime}: a stable hook. Optional: without
   * it a transcript still loads whenever its tab opens, it just doesn't refresh while open.
   */
  useInstanceRealtime?: (instanceId: string, handlers: InstanceRealtimeHandlers) => void;
}

/** Which interactive features are live vs. shown-but-disabled. */
export interface DialWorkspaceCapabilities {
  /**
   * `"preview"` currently has NO production consumer. It existed for web-admin's draft-preview overlay,
   * which KEY-515 deleted in favour of sending a dial for checking — and checking runs the real user app
   * against a real instance, so it is `"live"` like everything else. The mode is kept because it is the
   * default fixture in this package's own tests and is the one honest way to render the workspace
   * read-only against a model that has no server behind it. Don't go looking for the live path: there
   * isn't one. Delete it if that stops being worth the two branches it still costs.
   */
  mode: "live" | "preview";
  /** Files tab is interactive (vs. a disabled placeholder). */
  documents: boolean;
  /** Discussion tab is interactive (vs. a disabled placeholder). */
  messages: boolean;
  /** Subscribe controls are live (vs. shown but disabled). */
  subscriptions: boolean;
  /** Inline item add/remove/rename (the customisation overlay) is offered. */
  itemEditing: boolean;
  /** A group overview segment drills into its child instance (vs. opening the read-only panel). */
  groupDrillIn: boolean;
  /**
   * KEY-532: which workspace to draw. `"dynamic"` turns the selection round to East and projects the cards
   * from it; `"classic"` is the workspace as it has always been. `"dynamic3d"` (KEY-543) is dynamic with the
   * dial and card turned toward each other in a shared perspective — everything else about it is identical,
   * so it is a modifier on dynamic rather than a third rendering path.
   *
   * Optional, and absent means classic — so every existing host (and every fixture in this package's own
   * tests) keeps rendering exactly what it did. The choice itself belongs to the HOST: it is a per-person
   * server-stored preference behind a feature flag, neither of which this package knows about.
   */
  workspace?: "classic" | "dynamic" | "dynamic3d";
  /**
   * KEY-684: the server has push notifications switched on (`Features:PushNotifications`), so the subscribe menu
   * offers "Push to my phone". Optional and absent means off — a host that can't say must not offer a channel
   * the server would refuse for everyone. Whether THIS person has a phone registered is not known here; the
   * server refuses that opt-in with its own message, which the menu shows.
   */
  pushNotifications?: boolean;
  /**
   * Whether the card's tab strips show names or icons — one per-person, server-stored choice for every strip
   * (dial, section, action point, item). Optional and absent means names, so every existing host and fixture
   * renders as it did.
   */
  tabStrip?: TabStripDisplay;
  /**
   * Called when the reader flips the labels/icons toggle. Absent, no toggle is offered: a host that can't
   * store the choice shouldn't offer one that forgets itself.
   */
  onTabStripChange?: (next: TabStripDisplay) => void;
  /**
   * Look back as a compare mode (`Features:LookBackCompare`): a button on the search row puts the Then dial
   * beside the Now one, instead of the Look back tab in the card. Optional and absent means off, which keeps
   * the tab.
   */
  lookBackCompare?: boolean;
  /** KEY-888: Look ahead (`Features:LookAhead`) — a button beside Look back that puts the dial as it will be beside Now. */
  lookAhead?: boolean;
}

interface DialWorkspaceContextValue {
  services: WorkspaceServices;
  capabilities: DialWorkspaceCapabilities;
}

const DialWorkspaceContext = createContext<DialWorkspaceContextValue | null>(null);

export function DialWorkspaceProvider({
  services,
  capabilities,
  children,
}: {
  services: WorkspaceServices;
  capabilities: DialWorkspaceCapabilities;
  children: ReactNode;
}) {
  // Stable while services + capabilities are (hosts pass module-level constants), so consumers
  // don't re-render — and effects keyed off these values stay settled — on every host render.
  const value = useMemo(() => ({ services, capabilities }), [services, capabilities]);
  return <DialWorkspaceContext.Provider value={value}>{children}</DialWorkspaceContext.Provider>;
}

function useDialWorkspaceContext(): DialWorkspaceContextValue {
  const value = useContext(DialWorkspaceContext);
  if (!value) {
    throw new Error("DialWorkspace components must be rendered within <DialWorkspaceProvider>.");
  }
  return value;
}

export function useWorkspaceServices(): WorkspaceServices {
  return useDialWorkspaceContext().services;
}

export function useCapabilities(): DialWorkspaceCapabilities {
  return useDialWorkspaceContext().capabilities;
}

/**
 * The tab-strip display for a strip to spread onto TabStrip: `display`, plus `onDisplayChange` when this strip
 * is one that carries the toggle. The item card's compact strip follows the choice without offering its own
 * switch — one toggle per card is enough, and a second one inside it is just another thing to tap by mistake.
 */
export function useTabStripDisplay(withToggle = true): {
  display: TabStripDisplay;
  onDisplayChange?: (next: TabStripDisplay) => void;
} {
  // Read without the provider guard: a strip rendered on its own (a test, a story) just shows names.
  const caps = useContext(DialWorkspaceContext)?.capabilities;
  return {
    display: caps?.tabStrip ?? "labels",
    onDisplayChange: withToggle ? caps?.onTabStripChange : undefined,
  };
}
